import IReviewFormProps from './IReviewFormProps';
import React, { useEffect, useRef } from 'react';
import { Card, CardBody, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import FormStepper from '../../Form/FormStepper/FormStepper';
import { LoadingSpinner } from '../../LoadingSpinner';
import { useSupervisorReviewFormComponent } from './hooks/useSupervisorReviewFormComponent';
import IEvaluationFormStep from 'interfaces/IEvaluationFormStep';
import { FeedbackLevel } from '../../../interfaces/FeedbackLevel';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import ApplicationReviewFormatSelect from './ApplicationReviewFormatSelect';
import { ReviewFormat } from '../../../graphql/__generated__/graphql';

const PrimarySupervisorReviewForm: React.FC<IReviewFormProps> = ({ reviewSummary, readOnly }) => {
    const formRef = useRef(null);
    const navigate = useNavigate();
    const notificationContext = useNotificationContext();
    const { models, operations } = useSupervisorReviewFormComponent(reviewSummary, readOnly);

    const {
        formData,
        updating,
        currentStep,
        requirements,
        loading,
        applicationReview,
        operationResult,
    } = models;
    const {
        handleNextStep,
        handleInputChangeEvent,
        handleInputChange,
        handleSaveProgress,
        handleSetStep,
        handleSubmit,
        handleSetReviewFormat,
    } = operations;

    const handleFormFeedback = (level: FeedbackLevel, message?: string[] | string) =>
        notificationContext.notify(level, message);

    useEffect(() => {
        if (operationResult?.message) {
            handleFormFeedback(
                operationResult.success ? 'info' : 'danger',
                operationResult.message
            );
        }
    }, [operationResult]);

    if (!requirements || !requirements?.evaluation?.length || loading || updating)
        return <LoadingSpinner />;

    if (!readOnly && !applicationReview.isRenewal && !applicationReview.reviewFormat) {
        if (requirements.config?.algorithmEnabled && requirements.config?.minScore !== undefined) {
            return <ApplicationReviewFormatSelect onSelect={handleSetReviewFormat} />;
        }

        handleSetReviewFormat(ReviewFormat.Descriptive);
        return <LoadingSpinner />;
    }

    const openApplicationInNewTab = () => {
        localStorage.setItem(
            'application',
            JSON.stringify(models.applicationReview.accreditationApplication)
        );
        window.open('/accreditation/view', '_blank', 'noreferrer');
    };

    const renderStep = (Step: React.FC<IEvaluationFormStep>, stepNumber: number) => (
        <Step
            formData={formData}
            formRef={formRef}
            models={models}
            onFeedback={handleFormFeedback}
            handleInputChangeEvent={handleInputChangeEvent}
            handleNext={handleNextStep}
            handleSubmit={handleSubmit}
            onInputChange={handleInputChange}
            requirementsGroup={requirements.evaluation[stepNumber - 1]}
            requirements={requirements}
            readOnly={readOnly}
            reviewFormat={applicationReview.reviewFormat || ReviewFormat.Descriptive} // default to descriptive if not set
        />
    );

    if (!models.stepsConfig.length) {
        return <LoadingSpinner />;
    }

    const stepsConfig = models.stepsConfig.slice(
        0,
        readOnly ? models.stepsConfig.length - 1 : models.stepsConfig.length
    );

    return (
        <>
            <h2 className="fs-1 fw-bold mb-5">
                Supervisor evaluation for {reviewSummary.applicant.name}
            </h2>
            <h4>
                View {reviewSummary.applicant.name}'s application &nbsp;
                <a
                    href={'/accreditation/view'}
                    target={'_blank'}
                    rel={'noreferrer'}
                    onClick={(e) => {
                        e.preventDefault();
                        openApplicationInNewTab();
                    }}
                >
                    here
                </a>
                &nbsp;(opens in a new tab)
            </h4>
            {formData.comments && readOnly && (
                <Row>
                    <Card className="col-auto">
                        <CardBody>
                            <Card.Title>Supervisor comments</Card.Title>
                            {formData.comments}
                        </CardBody>
                    </Card>
                </Row>
            )}
            <Row>
                <Form
                    noValidate
                    ref={formRef}
                    onSubmit={() => operations.handleSubmit()}
                    action={'/accreditation'}
                >
                    <FormStepper
                        stepNumber={currentStep}
                        renderStep={renderStep}
                        steps={stepsConfig}
                        formRef={formRef}
                        allowSkipStep={true}
                        handleCanSkipToStep={() => true}
                        handleFeedback={handleFormFeedback}
                        handleChangeStep={handleSetStep}
                        handleCancel={() => {
                            navigate('/accreditation');
                        }}
                        handleSubmit={(e) => {
                            // The review form doesn't use a typical form submit
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        handleSaveProgress={handleSaveProgress}
                        submitted={false}
                        submitting={false}
                        allowSubmit={false}
                        readOnly={readOnly}
                        submitText={'Submit and pay'}
                        nextButtonText={readOnly ? 'Next' : 'Save and continue'}
                        className={'overflow-x-auto'}
                    ></FormStepper>
                    <Form.Control name={'formSubmitType'} value="evaluation" type={'hidden'} />
                    <Form.Control
                        name={'formSubmitResult'}
                        value={models.applicationReview.reviewStatus?.toLowerCase()}
                        type={'hidden'}
                    />
                </Form>
            </Row>
            {updating && (
                <Row>
                    <LoadingSpinner />
                </Row>
            )}
        </>
    );
};

export default PrimarySupervisorReviewForm;
